import type {
  CommissionReadinessReviewData,
  EligibleRevenueCandidate,
  RevenueAgentTask,
  RevenueAttribution,
  RevenueBillingRecord,
  RevenueCommissionEntry,
  RevenueOpportunity,
  RevenuePartner,
  RevenuePartnerPlanAssignment,
  RevenuePayoutItem,
  RevenuePayoutRecord,
  RevenuePartnerUserLink,
  RevenueStripeMirrorHealth,
  RevenueStripeWebhookEvent,
  RevenueOpsNotification,
  RevenueOpsNotificationCategory,
} from "../../types/revenueOps";

export type RevenueOpsActionBadgeCounts = Record<string, number>;

export type RevenueOpsActionBadgeInput = {
  billingRecords: RevenueBillingRecord[];
  partners: RevenuePartner[];
  partnerAssignments: RevenuePartnerPlanAssignment[];
  partnerUserLinks: RevenuePartnerUserLink[];
  opportunities: RevenueOpportunity[];
  attributions: RevenueAttribution[];
  commissionEntries: RevenueCommissionEntry[];
  commissionReadiness: CommissionReadinessReviewData;
  eligibleRevenueCandidates: EligibleRevenueCandidate[];
  payouts: RevenuePayoutRecord[];
  payoutItems: RevenuePayoutItem[];
  tasks: RevenueAgentTask[];
  stripeHealth: RevenueStripeMirrorHealth;
  stripeEvents?: RevenueStripeWebhookEvent[];
};

const openTask = (task: RevenueAgentTask) => !["completed", "dismissed", "acknowledged"].includes(task.status);
const taskReviewKey = (task: RevenueAgentTask) => {
  const payload = task.payload_json && typeof task.payload_json === "object" ? task.payload_json as Record<string, unknown> : {};
  return typeof payload.review_key === "string" ? payload.review_key : "";
};

export type StripeHealthActionItem = { review_key: string; label: string; reason: string; recommended_action: string; state: "action_needed" | "submitted_to_tj" | "tj_review_completed" | "acknowledged"; counted: boolean; livemode?: boolean };

function hasStripeTaskAcknowledgement(tasks: RevenueAgentTask[], reviewKey: string): RevenueAgentTask | undefined {
  return tasks.find((task) => ["stripe_webhook_failure_review", "stripe_mirror_integrity_review"].includes(task.task_type) && taskReviewKey(task) === reviewKey);
}

function isCompletedStripeTask(task: RevenueAgentTask): boolean {
  return ["completed", "dismissed", "acknowledged"].includes(task.status) || Boolean((task.payload_json as Record<string, unknown> | null | undefined)?.acknowledged);
}

function taskForReviewKey(tasks: RevenueAgentTask[], reviewKey: string): RevenueAgentTask | undefined {
  return tasks.find((task) => taskReviewKey(task) === reviewKey);
}

function notificationFromReview(input: {
  id: string;
  category: RevenueOpsNotificationCategory;
  label: string;
  reason: string;
  recommended_action: string;
  source_key: string;
  review_key: string;
  status: string;
  tasks: RevenueAgentTask[];
  can_send_to_tj?: boolean;
  can_acknowledge?: boolean;
  counts_in_overview?: boolean;
  mode?: RevenueOpsNotification["mode"];
}): RevenueOpsNotification {
  const task = taskForReviewKey(input.tasks, input.review_key);
  const completed = task ? isCompletedStripeTask(task) : false;
  const review_state = task ? (task.status === "dismissed" ? "dismissed" : completed ? "tj_completed" : "submitted_to_tj") : "action_needed";
  return {
    id: input.id,
    category: input.category,
    label: input.label,
    reason: input.reason,
    recommended_action: input.recommended_action,
    source_key: input.source_key,
    review_key: input.review_key,
    status: input.status,
    review_state,
    counts_in_overview: input.counts_in_overview !== false && !task,
    counts_in_tab_badge: !task,
    can_send_to_tj: input.can_send_to_tj !== false && !task,
    can_acknowledge: input.can_acknowledge !== false && !task,
    related_task_id: task?.id,
    mode: input.mode,
  };
}

export function deriveStripeHealthActionItems(health: RevenueStripeMirrorHealth, tasks: RevenueAgentTask[], events: RevenueStripeWebhookEvent[] = [], liveMode = true): StripeHealthActionItem[] {
  const eventById = new Map(events.map((event) => [event.stripe_event_id, event]));
  const items: StripeHealthActionItem[] = [];
  health.integrity_checks.forEach((check) => {
    if (!check.count || check.severity === "info") return;
    const samples = check.samples.length ? check.samples : Array.from({ length: check.count }, (_, index) => ({ review_key: `stripe_mirror_health:${check.key}:${index + 1}` }));
    samples.slice(0, check.count).forEach((sample, index) => {
      const event = sample.stripe_event_id ? eventById.get(sample.stripe_event_id) : undefined;
      const livemode = sample.livemode ?? event?.livemode;
      if (liveMode && livemode === false) return;
      const reviewKey = sample.review_key || (sample.stripe_event_id ? `stripe_mirror_event:${sample.stripe_event_id}:${sample.event_type || "unknown"}:failed` : `stripe_mirror_integrity:${check.key}:${sample.id || index}`);
      const task = hasStripeTaskAcknowledgement(tasks, reviewKey);
      const state = task ? (isCompletedStripeTask(task) ? "tj_review_completed" : "submitted_to_tj") : "action_needed";
      items.push({ review_key: reviewKey, label: check.label, reason: sample.detail || check.explanation, recommended_action: "Review the mirror item or add it to the TJ manual queue.", state, counted: !task, livemode });
    });
  });
  return items;
}

export function hasReviewTaskAcknowledgement(tasks: RevenueAgentTask[], reviewKey: string): boolean {
  return Boolean(reviewKey) && tasks.some((task) => task.task_type === "eligible_revenue_review" && taskReviewKey(task) === reviewKey);
}

export function getReviewTaskStatus(tasks: RevenueAgentTask[], reviewKey: string): RevenueAgentTask["status"] | undefined {
  return tasks.find((task) => task.task_type === "eligible_revenue_review" && taskReviewKey(task) === reviewKey)?.status;
}

export function isEligibleRevenueCandidateActionable(candidate: EligibleRevenueCandidate, tasks: RevenueAgentTask[]): boolean {
  if (candidate.status === "ready") return true;
  return ["blocked", "review_needed"].includes(candidate.status) && !hasReviewTaskAcknowledgement(tasks, candidate.review_key);
}

export function deriveRevenueOpsNotifications(input: RevenueOpsActionBadgeInput): RevenueOpsNotification[] {
  const notifications: RevenueOpsNotification[] = [];
  const push = (item: Parameters<typeof notificationFromReview>[0]) => notifications.push(notificationFromReview(item));
  deriveStripeHealthActionItems(input.stripeHealth, input.tasks, input.stripeEvents).forEach((item) => push({
    id: `stripe:${item.review_key}`,
    category: /product|price|line/i.test(item.label) ? "billing" : "stripe_health",
    label: item.label,
    reason: item.reason,
    recommended_action: item.recommended_action,
    source_key: item.review_key,
    review_key: item.review_key,
    status: item.state,
    tasks: input.tasks,
    mode: item.livemode === false ? "test" : item.livemode === true ? "live" : "unknown",
  }));
  input.eligibleRevenueCandidates.filter((candidate) => ["ready", "blocked", "review_needed"].includes(candidate.status)).forEach((candidate) => push({
    id: `eligible:${candidate.review_key}`,
    category: "revenue_review",
    label: candidate.invoice_number || "Eligible revenue candidate",
    reason: candidate.reasons.join(" ") || (candidate.status === "ready" ? "Ready for eligibility staging." : "Manual eligibility review is required."),
    recommended_action: candidate.status === "ready" ? "Stage for eligibility review." : "Add to TJ review queue.",
    source_key: candidate.review_key,
    review_key: candidate.review_key,
    status: candidate.status,
    tasks: input.tasks,
    can_send_to_tj: candidate.status !== "ready",
    can_acknowledge: candidate.status !== "ready",
  }));
  input.opportunities.filter((item) => !item.archived_at && !["won", "closed_won", "lost", "closed_lost", "duplicate", "rejected", "rejected_duplicate"].includes(item.stage)).forEach((opportunity) => {
    if (opportunity.request_type && opportunity.request_status === "pending_review") push({ id: `opportunity-request:${opportunity.id}`, category: "opportunities", label: "Partner opportunity request", reason: "A partner-submitted opportunity request is awaiting admin review.", recommended_action: "Review the opportunity request.", source_key: opportunity.id, review_key: `opportunity_request:${opportunity.id}`, status: opportunity.request_status, tasks: input.tasks, can_acknowledge: false });
    if (opportunity.duplicate_review || opportunity.attribution_review === "pending_review") push({ id: `opportunity-review:${opportunity.id}`, category: "opportunities", label: "Opportunity review needed", reason: opportunity.duplicate_review ? "A possible duplicate opportunity needs review." : "Opportunity attribution requires review.", recommended_action: "Review the opportunity before partner assignment.", source_key: opportunity.id, review_key: `opportunity_review:${opportunity.id}`, status: "review_needed", tasks: input.tasks, can_acknowledge: false });
    if (opportunity.partner_id && opportunity.ownership_window_status === "expired") push({ id: `ownership:${opportunity.id}`, category: "ownership", label: "Expired ownership window", reason: "Partner ownership appears expired and needs admin review.", recommended_action: "Review activity and ownership before approving a claim.", source_key: opportunity.id, review_key: `ownership_expired:${opportunity.id}`, status: "expired", tasks: input.tasks, can_acknowledge: false });
  });
  input.attributions.filter((item) => ["pending", "disputed"].includes(item.status)).forEach((attribution) => push({ id: `attribution:${attribution.id}`, category: "attribution", label: "Attribution review", reason: attribution.status === "disputed" ? "This attribution is disputed." : "This attribution is awaiting review.", recommended_action: "Review attribution and effective dates.", source_key: attribution.id, review_key: `attribution_review:${attribution.id}`, status: attribution.status, tasks: input.tasks, can_send_to_tj: false }));
  input.commissionEntries.filter((entry) => ["pending", "pending_review"].includes(entry.status) && (entry.review_status || "pending_review") === "pending_review").forEach((entry) => push({ id: `commission:${entry.id}`, category: "commissions", label: "Draft commission review", reason: "A draft commission entry is pending manual review.", recommended_action: "Review the draft commission entry.", source_key: entry.id, review_key: `commission_review:${entry.id}`, status: entry.status, tasks: input.tasks, can_send_to_tj: false }));
  input.commissionReadiness.candidates.filter((candidate) => ["blocked", "review_needed", "ready"].includes(candidate.status)).forEach((candidate) => push({ id: `readiness:${candidate.eligible_revenue_event_id}`, category: "commissions", label: "Commission readiness review", reason: candidate.reasons.join(" ") || "Commission readiness requires preflight review.", recommended_action: candidate.status === "ready" ? "Create a draft commission entry after review." : "Review the commission readiness blockers.", source_key: candidate.source_key, review_key: `commission_readiness:${candidate.eligible_revenue_event_id}`, status: candidate.status, tasks: input.tasks, can_send_to_tj: candidate.status !== "ready" }));
  const batchedEntries = new Set(input.payoutItems.map((item) => item.commission_entry_id));
  input.commissionEntries.filter((entry) => entry.status === "approved" && !batchedEntries.has(entry.id)).forEach((entry) => push({ id: `payout-entry:${entry.id}`, category: "payouts", label: "Approved commission not batched", reason: "An approved commission is ready for payout preparation but is not in a payout batch.", recommended_action: "Review and select it for a manual payout batch.", source_key: entry.id, review_key: `payout_entry:${entry.id}`, status: entry.status, tasks: input.tasks, can_send_to_tj: false }));
  input.payouts.filter((batch) => ["draft", "prepared", "pending_review", "approved"].includes(batch.status) || batch.item_count === 0).forEach((batch) => push({ id: `payout:${batch.id}`, category: "payouts", label: "Payout preparation review", reason: batch.item_count === 0 ? "This payout batch has no items." : "This payout batch is prepared but not externally paid.", recommended_action: "Review payout preparation before external payment.", source_key: batch.id, review_key: `payout_review:${batch.id}`, status: batch.status, tasks: input.tasks, can_send_to_tj: false }));
  input.billingRecords.filter((record) => record.account_mapping_issue || record.failed_payment).forEach((record) => push({ id: `billing:${record.id}`, category: "billing", label: record.failed_payment ? "Failed payment" : "Billing account mapping", reason: record.failed_payment ? "A failed payment needs review." : "A billing customer is not mapped to a core account.", recommended_action: "Review billing state and account mapping.", source_key: record.id, review_key: `billing_review:${record.id}`, status: "review_needed", tasks: input.tasks, can_send_to_tj: false }));
  input.partners.filter((partner) => partner.status === "active" && !input.partnerAssignments.some((assignment) => assignment.partner_id === partner.id && (!assignment.ends_at || assignment.ends_at >= new Date().toISOString().slice(0, 10)))).forEach((partner) => push({ id: `partner:${partner.id}`, category: "partners", label: "Partner setup review", reason: "An active partner has no active commission plan assignment.", recommended_action: "Assign or review the partner commission plan.", source_key: partner.id, review_key: `partner_setup:${partner.id}`, status: "review_needed", tasks: input.tasks, can_send_to_tj: false }));
  input.partners.filter((partner) => partner.tax_profile_status && ["not_started", "incomplete", "needs_cpa_review"].includes(partner.tax_profile_status)).forEach((partner) => push({ id: `tax:${partner.id}`, category: "tax", label: "1099 readiness review", reason: "Partner tax readiness is incomplete or needs CPA review.", recommended_action: "Review tax profile readiness before export.", source_key: partner.id, review_key: `tax_readiness:${partner.id}`, status: partner.tax_profile_status || "review_needed", tasks: input.tasks, can_send_to_tj: false }));
  input.tasks.filter(openTask).forEach((task) => notifications.push({ id: `task:${task.id || task.title}`, category: "agent_tasks", label: task.title, reason: task.summary || "An internal task is open.", recommended_action: "Complete or dismiss the task.", source_key: task.id || task.title, review_key: taskReviewKey(task), status: task.status, review_state: "submitted_to_tj", counts_in_overview: false, counts_in_tab_badge: true, can_send_to_tj: false, can_acknowledge: false, related_task_id: task.id }));
  return notifications;
}

export function deriveRevenueOpsActionBadgeCounts(input: RevenueOpsActionBadgeInput): RevenueOpsActionBadgeCounts {
  const notifications = deriveRevenueOpsNotifications(input);
  const count = (category: RevenueOpsNotificationCategory) => notifications.filter((item) => item.category === category && item.counts_in_tab_badge).length;
  return {
    overview: notifications.filter((item) => item.counts_in_overview).length,
    billing: count("billing"),
    partners: count("partners"),
    opportunities: count("opportunities") + count("ownership"),
    attribution: count("attribution"),
    commissions: count("commissions"),
    payouts: count("payouts"),
    tax: count("tax"),
    agent_tasks: count("agent_tasks"),
    eligible_revenue: count("revenue_review"),
    ownership: count("ownership"),
    audit_log: 0,
  };
}

export function formatRevenueOpsActionBadgeCount(count: number): string {
  return count > 99 ? "99+" : String(Math.max(0, count));
}
